import { useCallback, useEffect, useRef, useState } from 'react';

import { ProvisioningCandidate } from './deviceProvisioning';
import { scanBleProvisioningDevices } from './bleProvisioningTransport';

export function useBleProvisioningScan(enabled: boolean) {
    const [devices, setDevices] = useState<ProvisioningCandidate[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [isScanning, setIsScanning] = useState(false);
    const [scanNonce, setScanNonce] = useState(0);
    const controllerRef = useRef<AbortController | null>(null);
    const scanPromiseRef = useRef<Promise<void> | null>(null);

    const retry = useCallback(() => {
        setDevices([]);
        setError(null);
        setScanNonce(nonce => nonce + 1);
    }, []);

    const stop = useCallback(async () => {
        controllerRef.current?.abort();
        setIsScanning(false);

        try {
            await scanPromiseRef.current;
        }
        catch {
            // The scan promise may reject while shutting down; callers only need scanning to stop.
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const controller = new AbortController();
        controllerRef.current = controller;
        setIsScanning(true);
        setError(null);

        const scanPromise = scanBleProvisioningDevices(candidate => {
            setDevices(current => {
                const next = current.filter(device => device.id !== candidate.id);
                next.push(candidate);
                return next.sort((a, b) => (b.rssi ?? -Infinity) - (a.rssi ?? -Infinity));
            });
        }, controller.signal);
        scanPromiseRef.current = scanPromise;

        scanPromise
            .catch(e => {
                if (!controller.signal.aborted) {
                    setError(e instanceof Error ? e : new Error(String(e)));
                }
            })
            .finally(() => {
                if (controllerRef.current === controller) {
                    controllerRef.current = null;
                    scanPromiseRef.current = null;
                }

                if (!controller.signal.aborted) {
                    setIsScanning(false);
                }
            });

        return () => {
            controller.abort();
            setIsScanning(false);
        };
    }, [enabled, scanNonce]);

    return {
        devices,
        error,
        isScanning,
        retry,
        stop
    };
}
